import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import { CartesianGrid, XAxis, YAxis, Legend, LineChart, Line, PieChart, Pie, Cell, Tooltip as RechartsTooltip } from "recharts";
import { Checkbox } from "@shared-components/shadcn/checkbox";
import { Button } from "@shared-components/shadcn/button";
import _ from "lodash";
import { formatGranularDateTick } from "./metric-utils";
import { AiProfileModelEnumValues, getModelMetadata, type AiProfileModelEnumType } from "@shared-utils/ai/configuration/ai-model-config";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
const chartConfig: ChartConfig = {
  count: {
    label: "AI models count in chats"
  }
};
interface AiModelsCountInChatsProps {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
}
const [reasoningModels, nonReasoningModels] = _.partition(AiProfileModelEnumValues, model => getModelMetadata(model).isReasoning);

// Define which models are active by default
const INITIAL_VISIBLE_MODELS = new Set<AiProfileModelEnumType>(["GPT_4o", "GPT_4.1", "GPT_o4_MINI", "GPT_o3_MINI", "GPT_o3"]);
export const AiModelsCountInChats = props => {
  const $ = _c(63);
  let t0;
  if ($[0] !== props.endDate || $[1] !== props.granularity || $[2] !== props.startDate) {
    t0 = {
      startDate: props.startDate,
      endDate: props.endDate,
      granularity: props.granularity
    };
    $[0] = props.endDate;
    $[1] = props.granularity;
    $[2] = props.startDate;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const coreMetricsQuery = api.coreMetrics.aiModelTypesCountInChats.useQuery(t0);
  let t1;
  if ($[4] !== coreMetricsQuery.data?.chartData) {
    t1 = coreMetricsQuery.data?.chartData ?? [];
    $[4] = coreMetricsQuery.data?.chartData;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const chartData = t1;
  const [visibleModels, setVisibleModels] = useState(INITIAL_VISIBLE_MODELS);
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = model => {
      setVisibleModels(current => {
        if (current.has(model)) {
          current.delete(model);
        } else {
          current.add(model);
        }
        return new Set(current);
      });
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleToggle = t2;
  const normalizedChartData = chartData;
  let t3;
  if ($[7] !== coreMetricsQuery.data?.modelTotals) {
    t3 = coreMetricsQuery.data?.modelTotals ?? {} as Record<AiProfileModelEnumType, number>;
    $[7] = coreMetricsQuery.data?.modelTotals;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const modelTotals = t3;
  let t4;
  if ($[9] !== modelTotals) {
    t4 = AiProfileModelEnumValues.filter(model_0 => (modelTotals[model_0] ?? 0) > 0).map(model_1 => {
      const metadata = getModelMetadata(model_1);
      return {
        name: metadata.name,
        value: modelTotals[model_1],
        color: metadata.color
      };
    });
    $[9] = modelTotals;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const pieChartData = t4;
  let t5;
  if ($[11] !== pieChartData) {
    t5 = () => {
      const enabledModelsList = pieChartData.map(_temp);
      setVisibleModels(new Set(enabledModelsList));
    };
    $[11] = pieChartData;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleEnablePieModels = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h2 className="mb-4">{"AI models count in chats"}</h2>;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mb-1 text-xs">{"Reasoning models"}</div>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== visibleModels) {
    t8 = reasoningModels.map(model_2 => <ModelLabel key={model_2} model={model_2} visibleModels={visibleModels} handleToggle={handleToggle} />);
    $[15] = visibleModels;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <div>{t7}<div className="flex flex-wrap gap-3">{t8}</div></div>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mb-1 text-xs">{"Non-reasoning models"}</div>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== visibleModels) {
    t11 = nonReasoningModels.map(model_3 => <ModelLabel key={model_3} model={model_3} visibleModels={visibleModels} handleToggle={handleToggle} />);
    $[20] = visibleModels;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== handleEnablePieModels) {
    t12 = <Button onClick={handleEnablePieModels}>{"Enable models for selected time"}</Button>;
    $[22] = handleEnablePieModels;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <div>{t10}<div className="flex flex-wrap gap-3">{t11}{t12}</div></div>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== t9) {
    t14 = <div className="mb-4 flex flex-col gap-8 sm:flex-row sm:gap-8">{t9}{t13}</div>;
    $[27] = t13;
    $[28] = t9;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <CartesianGrid vertical={false} />;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== props.granularity) {
    t16 = <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} fill="#006e96" minTickGap={32} tickFormatter={value => formatGranularDateTick(value, props.granularity)} />;
    $[31] = props.granularity;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <YAxis yAxisId="count" interval={1} orientation="left" domain={[0, "auto"]} />;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ChartTooltip content={<ChartTooltipContent className="w-[150px]" nameKey="date" labelFormatter={_temp2} />} />;
    t19 = <Legend />;
    $[34] = t18;
    $[35] = t19;
  } else {
    t18 = $[34];
    t19 = $[35];
  }
  let t20;
  if ($[36] !== visibleModels) {
    t20 = AiProfileModelEnumValues.filter(model_4 => visibleModels.has(model_4)).map(_temp3);
    $[36] = visibleModels;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== normalizedChartData || $[39] !== t16 || $[40] !== t20) {
    t21 = <div className="mt-8 flex w-full items-start"><ChartContainer config={chartConfig} className="aspect-auto h-[250px] flex-1"><LineChart data={normalizedChartData}>{t15}{t16}{t17}{t18}{t19}{t20}</LineChart></ChartContainer></div>;
    $[38] = normalizedChartData;
    $[39] = t16;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== pieChartData || $[43] !== visibleModels) {
    let t23;
    if ($[45] !== visibleModels) {
      t23 = entry_0 => visibleModels.has(entry_0.name);
      $[45] = visibleModels;
      $[46] = t23;
    } else {
      t23 = $[46];
    }
    t22 = pieChartData.filter(t23);
    $[42] = pieChartData;
    $[43] = visibleModels;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[47] !== pieChartData || $[48] !== visibleModels) {
    let t24;
    if ($[50] !== visibleModels) {
      t24 = entry_1 => visibleModels.has(entry_1.name);
      $[50] = visibleModels;
      $[51] = t24;
    } else {
      t24 = $[51];
    }
    t23 = pieChartData.filter(t24).map(_temp5);
    $[47] = pieChartData;
    $[48] = visibleModels;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[52] !== t22 || $[53] !== t23) {
    t24 = <Pie data={t22} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={_temp4}>{t23}</Pie>;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <RechartsTooltip />;
    t26 = <Legend />;
    $[55] = t25;
    $[56] = t26;
  } else {
    t25 = $[55];
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t24) {
    t27 = <div className="flex h-full items-center justify-center"><PieChart width={600} height={250}>{t24}{t25}{t26}</PieChart></div>;
    $[57] = t24;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t14 || $[60] !== t21 || $[61] !== t27) {
    t28 = <div>{t6}{t14}{t21}{t27}</div>;
    $[59] = t14;
    $[60] = t21;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  return t28;
};
const ModelLabel = props => {
  const $ = _c(16);
  const t0 = props.model;
  let t1;
  if ($[0] !== props.model || $[1] !== props.visibleModels) {
    t1 = props.visibleModels.has(props.model);
    $[0] = props.model;
    $[1] = props.visibleModels;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props) {
    t2 = () => props.handleToggle(props.model);
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Checkbox checked={t1} onCheckedChange={t2} className="accent-current" />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== props.model) {
    t4 = getModelMetadata(props.model);
    $[8] = props.model;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4.displayName) {
    t5 = <span>{t4.displayName}</span>;
    $[10] = t4.displayName;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props.model || $[13] !== t3 || $[14] !== t5) {
    t6 = <label key={t0} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50">{t3}{t5}</label>;
    $[12] = props.model;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
function _temp(entry) {
  return entry.name;
}
function _temp2(value_0) {
  return value_0;
}
function _temp3(model_5) {
  const metadata_0 = getModelMetadata(model_5);
  const key = `modelData.${model_5}`;
  return <Line key={key} yAxisId="count" type="monotone" dataKey={key} name={metadata_0.displayName} stroke={metadata_0.color} strokeWidth={2} dot={false} />;
}
function _temp4(t0) {
  const {
    name,
    value: value_1
  } = t0;
  return `${name}: (${value_1})`;
}
function _temp5(entry_2, idx) {
  return <Cell key={`cell-${idx}`} fill={entry_2.color} />;
}